import { Suspense, lazy } from "react";
import { Route, Navigate, useLocation } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { permissionService } from "./services/permissionService";
import { renderSkinHairAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";
import FranchisePassportGate from "./components/FranchisePassportGate";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const IbDashboard = lazy(() => import("./pages/IbDashboard"));
const CheckIns = lazy(() => import("./pages/CheckIns"));
const PartnerDeskGuestRegister = lazy(() => import("./pages/PartnerDeskGuestRegister"));
const PassportGuestRegister = lazy(() => import("./pages/PassportGuestRegister"));
const PassportGuestEdit = lazy(() => import("./pages/PassportGuestEdit"));
const PartnerOperationsAppointments = lazy(() => import("./pages/PartnerOperationsAppointments"));
const PartnerCheckinAnalytics = lazy(() => import("./pages/PartnerCheckinAnalytics"));
const VisitServices = lazy(() => import("./pages/VisitServices"));
const Customers = lazy(() => import("./pages/Customers"));
const PartnerCustomerAnalytics = lazy(() => import("./pages/PartnerCustomerAnalytics"));
const PartnerFlowAnalytics = lazy(() => import("./pages/PartnerFlowAnalytics"));
const PartnerServiceAnalytics = lazy(() => import("./pages/PartnerServiceAnalytics"));
const PartnerStaffAnalytics = lazy(() => import("./pages/PartnerStaffAnalytics"));
const StaffReport = lazy(() => import("./pages/StaffReport"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const KioskLoginCredentials = lazy(() => import("./pages/KioskLoginCredentials"));
const AppsDownload = lazy(() => import("./pages/AppsDownload"));
const DashboardStaffUsers = lazy(() => import("./pages/DashboardStaffUsers"));
const StoreLocation = lazy(() => import("./pages/StoreLocation"));
const PriceList = lazy(() => import("./pages/PriceList"));
const FranchiseProductList = lazy(() => import("./pages/FranchiseProductList"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Staff = lazy(() => import("./pages/Staff"));
const StaffAvailability = lazy(() => import("./pages/StaffAvailability"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Contact = lazy(() => import("./pages/Contact"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const OverviewSectionHubPage = lazy(() => import("../admin-shared/pages/OverviewSectionHubPage"));
const EngagementApp = lazy(() => import("../engagement/EngagementApp"));

const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/nyafaz-ep/partner/overview" replace />;
  }
  return children;
};

function PartnerEngagementLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/naturals\/partner\/demo-engagement\/?/, "");
  const target = rest
    ? `/nyafaz-ep/partner/engagement/${rest}`
    : "/nyafaz-ep/partner/engagement/";
  return <Navigate to={`${target}${location.search || ""}`} replace />;
}

export function renderPartnerRoutes({ partnerHome = false } = {}) {
  return (
    <>
      {partnerHome ? <Route path="/" element={<Navigate to="/nyafaz-ep/partner" replace />} /> : null}
      <Route path="/nyafaz-ep/partner" element={<Navigate to="/nyafaz-ep/login" replace />} />
      <Route
        path="/nyafaz-ep/partner/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="partner" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/partner/overview/:sectionId"
        element={
          <Layout>
            {withSuspense(
              <OverviewSectionHubPage portal="partner" permissionService={permissionService} />,
            )}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/partner/ib-dashboard"
        element={<Layout dashboardOnly>{withSuspense(<IbDashboard />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/operations/appointments"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerOperationsAppointments />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/guest/appointments"
        element={<Navigate to="/nyafaz-ep/partner/operations/appointments" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/engagement/appointments"
        element={<Navigate to="/nyafaz-ep/partner/operations/appointments" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/engagement/appointments/*"
        element={<Navigate to="/nyafaz-ep/partner/operations/appointments" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/engagement/*"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/engagement/"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/engagement"
        element={<Navigate to="/nyafaz-ep/partner/engagement/" replace />}
      />
      <Route path="/nyafaz-ep/partner/demo-engagement/*" element={<PartnerEngagementLegacyRedirect />} />
      <Route
        path="/nyafaz-ep/partner/demo-engagement"
        element={<Navigate to="/nyafaz-ep/partner/engagement/" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/check-ins"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<CheckIns />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/desk-guest-register"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerDeskGuestRegister />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/passport-register/:checkinId"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <FranchisePassportGate>
              <Layout>{withSuspense(<PassportGuestRegister />)}</Layout>
            </FranchisePassportGate>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/passport-edit/:checkinId"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <FranchisePassportGate>
              <Layout>{withSuspense(<PassportGuestEdit />)}</Layout>
            </FranchisePassportGate>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/check-in-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerCheckinAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/visit-services"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<VisitServices />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/customers"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<Customers />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/customer-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerCustomerAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/flow-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessFlowAnalytics()}>
            <Layout>{withSuspense(<PartnerFlowAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/service-analytics"
        element={
          <PermRoute
            check={() =>
              permissionService.canAccessSkinAnalytics() ||
              permissionService.canAccessHairAnalytics()
            }
          >
            <Layout>{withSuspense(<PartnerServiceAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/staff-report"
        element={<Layout>{withSuspense(<StaffReport />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/staff-analytics"
        element={<Navigate to="/nyafaz-ep/partner/staff-report" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/customers/:faceUserId"
        element={<Layout>{withSuspense(<CustomerDetail />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/kiosk-login"
        element={
          <PermRoute check={() => permissionService.canAccessKioskSkinStack()}>
            <Layout>{withSuspense(<KioskLoginCredentials />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/guest-qr"
        element={<Navigate to="/nyafaz-ep/partner/apps" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/apps"
        element={
          <PermRoute check={() => !permissionService.isDashboardStaff()}>
            <Layout>{withSuspense(<AppsDownload />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/dashboard-staff"
        element={
          <PermRoute check={() => permissionService.canManageDashboardStaff()}>
            <Layout>{withSuspense(<DashboardStaffUsers />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/reels"
        element={<Navigate to="/nyafaz-ep/partner/overview" replace />}
      />
      <Route path="/nyafaz-ep/partner/reels-app" element={<Navigate to="/nyafaz-ep/partner/apps" replace />} />
      <Route path="/nyafaz-ep/partner/salon-app" element={<Navigate to="/nyafaz-ep/partner/apps" replace />} />
      <Route path="/nyafaz-ep/partner/passport-app" element={<Navigate to="/nyafaz-ep/partner/apps" replace />} />
      <Route
        path="/nyafaz-ep/partner/store-location"
        element={<Layout>{withSuspense(<StoreLocation />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/price-list"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<PriceList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/product-list"
        element={
          <PermRoute check={() => permissionService.canAccessProductCatalogAdmin()}>
            <Layout>{withSuspense(<FranchiseProductList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/partner/my-orders"
        element={<Layout hideSidebar>{withSuspense(<MyOrders />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/productlist"
        element={<Navigate to="/nyafaz-ep/partner/product-list" replace />}
      />
      <Route
        path="/nyafaz-ep/partner/staff"
        element={<Layout>{withSuspense(<Staff />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/staff-availability"
        element={<Layout>{withSuspense(<StaffAvailability />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/partner/naturals-kpis"
        element={<Layout>{withSuspense(<NaturalsKpis />)}</Layout>}
      />
      <Route path="/nyafaz-ep/partner/complaints" element={<Layout>{withSuspense(<Complaints />)}</Layout>} />
      <Route path="/nyafaz-ep/partner/edit-profile" element={<Layout>{withSuspense(<EditProfile />)}</Layout>} />
      <Route path="/nyafaz-ep/partner/contact-us" element={<Layout>{withSuspense(<Contact />)}</Layout>} />
      {renderSkinHairAnalyticsRoutes({
        portalId: "partner",
        basePath: "/nyafaz-ep/partner",
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
      })}
    </>
  );
}
